
import gql from 'graphql-tag'
import {
    GetSinglePostingQueryVariables,
    useGetSinglePostingQuery,
    PostingPriceGetHistoryQueryVariables,
    usePostingPriceGetHistoryQuery,
    usePostingPriceCreateOrUpdateMutation,
    PostingPriceCreateOrUpdateMutationVariables,
    usePostingPriceDeleteMutation,
    usePostingToggleArchivedMutation,
    usePostingSetArchivedMutation,
    PostingPriceInput
} from '../../generated/graphql'
import useForm from 'react-hook-form'
import propName from "../../helpers/propName"
import * as yup from "yup"
import moment from 'moment'
import React, { ReactElement, useState } from 'react'
import { Trash } from 'react-feather'
import HookedTextInput from '../../components/General/Inputs/HookedTextInput'
import HookedDateTime from '../../components/General/Inputs/HookedDateTime'
import Button, { ButtonType } from '../../components/General/Button/Button'
import FormPage from '../../components/General/FormPage/FormPage'
import FormTile from '../../components/Tiles/FormTile/FormTile'
import { Params, useNavigate, useParams } from 'react-router-dom'
import { showSuccessToast, showErrorToast } from '../../utils/errorUtils'
import DeleteEntityModal from '../../components/AppliedModals/DeleteEntity/DeleteEntityModal'
import styles from './PostingEdit.module.scss'

// GraphQL queries and mutations
export const GetSinglePosting = gql`
    fragment postingInfo on Posting {
        postingKey
        supplierKey
        supplier {
            name
        }
        terminalKey
        terminal {
            name
        }
        isArchived
        currentPrice {
            postingPriceKey
            cost
            effectiveDate
        }
    }

    query GetSinglePosting($id: Int!) {
        postingGetSingle(id: $id) {
            ...postingInfo
        }
    }
`

const PostingPriceOperations = gql`
    fragment postingPriceInfo on PostingPrice {
        postingPriceKey
        effectiveDate
        cost
        postingKey
    }

    query PostingPriceGetHistory($postingKey: Int!) {
        postingPriceGetHistory(postingKey: $postingKey) {
            ...postingPriceInfo
        }
    }

    mutation PostingPriceCreateOrUpdate($postingPrice: PostingPriceCreateOrUpdateInput!) {
        postingPriceCreateOrUpdate(postingPrice: $postingPrice) {
            ...postingPriceInfo
        }
    }

    mutation PostingPriceDelete($id: Int!) {
        postingPriceDelete(id: $id)
    }

    mutation PostingToggleArchived($postingKey: Int!) {
        postingToggleArchived(postingKey: $postingKey)
    }

    mutation PostingSetArchived($postingKey: Int!, $isArchived: Boolean!) {
        postingSetArchived(postingKey: $postingKey, isArchived: $isArchived)
    }
`

interface MatchParams extends Params {
    postingKey: string
}

interface Props {
}

interface PriceFormData {
    postingPrice: {
        cost: string
        effectiveDate: Date
        postingKey: number
    }
}

export default function PostingEdit(props: Props): ReactElement {
    const navigate = useNavigate()
    const { postingKey: key } = useParams() as MatchParams
    const postingKey = parseInt(key)

    const [currentlyDeletingPrice, setCurrentlyDeletingPrice] = useState<number | null>(null)
    const [showAllPrices, setShowAllPrices] = useState(false)
    const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<Date>(new Date())
    const [isSetup, setIsSetup] = useState(false)

    // Validation schema for price form
    const validationSchema = yup.object().shape({
        postingPrice: yup.object().shape({
            cost: yup.string().required("Cost is required").test('is-positive', 'Cost must be positive', value => {
                const num = parseFloat(value || '0')
                return !isNaN(num) && num >= 0
            }),
            effectiveDate: yup.date().required("Effective date is required")
        })
    })

    // GraphQL hooks
    const { data: postingData, loading: postingLoading, error: postingError, refetch: refetchPosting } = useGetSinglePostingQuery({
        variables: { id: postingKey }
    })

    // PostingPrice hooks - now using real queries
    const { data: priceHistoryData, loading: priceHistoryLoading, error: priceHistoryError, refetch: refetchPriceHistory } = usePostingPriceGetHistoryQuery({
        variables: { postingKey }
    })

    const [createOrUpdatePrice, { loading: mutationLoading, error: mutationError }] = usePostingPriceCreateOrUpdateMutation()
    const [deletePrice] = usePostingPriceDeleteMutation()
    const [togglePostingArchived] = usePostingToggleArchivedMutation()
    const [setPostingArchived] = usePostingSetArchivedMutation()

    // Form setup
    const { register, handleSubmit, errors, setValue, reset, getValues } = useForm<PriceFormData>({
        validationSchema,
        defaultValues: {
            postingPrice: {
                postingKey,
                effectiveDate: new Date(),
                cost: "0.0000"
            }
        }
    })

    const commonProps = { register, errors, setValue }


    const navigateToList = () => {
        navigate("/settings/postings")
    }

    // Use the current price computed by the backend (which uses Eastern timezone logic)
    const currentPrice = postingData?.postingGetSingle?.currentPrice

    // Update form when current price data loads - one time setup
    React.useEffect(() => {
        if (currentPrice && !isSetup) {
            setValue('postingPrice.cost' as any, currentPrice.cost.toFixed(4))
            setValue('postingPrice.effectiveDate' as any, new Date(moment.utc(currentPrice.effectiveDate).format('LL')))
            setSelectedEffectiveDate(new Date(moment.utc(currentPrice.effectiveDate).format('LL')))
            setIsSetup(true)
        }
    }, [currentPrice, setValue, isSetup])

    // Form submission handler
    const onSubmit = handleSubmit(async (variables: PriceFormData) => {
        try {
            // Check if we're updating today's price or creating a new one
            const isUpdatingToday = currentPrice &&
                moment(currentPrice.effectiveDate).isSame(moment(variables.postingPrice.effectiveDate), 'day')

            const mutationVariables = {
                postingPrice: {
                    effectiveDate: variables.postingPrice.effectiveDate,
                    effectiveEndDate: new Date('2099-12-31'), // Far future date since prices don't expire
                    cost: parseFloat(variables.postingPrice.cost), // Convert string to number
                    postingKey: postingKey, // Ensure postingKey is always set
                    ...(isUpdatingToday && currentPrice && { postingPriceKey: currentPrice.postingPriceKey })
                }
            }

            const result = await createOrUpdatePrice({
                variables: mutationVariables
            })

            showSuccessToast("Price updated successfully")
            refetchPriceHistory()
            refetchPosting() // Refresh the current price section

            // Reset form with current price if it exists, otherwise keep the form as is
            if (currentPrice) {
                reset({
                    postingPrice: {
                        postingKey,
                        effectiveDate: new Date(currentPrice.effectiveDate),
                        cost: currentPrice.cost.toFixed(4) // Convert to string with 4 decimals
                    }
                })
            }
        } catch (error) {
            console.error("Price update error:", error)
            showErrorToast(error as any)
        }
    })

    // Delete handlers
    const confirmDeletePrice = async (id: number) => {
        try {
            await deletePrice({ variables: { id } })
            showSuccessToast("Price deleted successfully")
            refetchPriceHistory()
            refetchPosting() // Refresh the current price section
            setCurrentlyDeletingPrice(null)
        } catch (error) {
            showErrorToast(error as any)
            console.error("Price delete error:", error)
        }
    }

    const cancelDeletePrice = () => {
        setCurrentlyDeletingPrice(null)
    }

    const handleToggleArchivedStatus = async () => {
        try {
            await togglePostingArchived({ variables: { postingKey } })
            showSuccessToast(`Posting ${posting.isArchived ? 'unarchived' : 'archived'} successfully`)
            refetchPosting()
        } catch (error) {
            showErrorToast(error as any)
            console.error("Toggle archived status error:", error)
        }
    }

    const dateChange = (name, val) => {
        if (val !== selectedEffectiveDate) {
            setSelectedEffectiveDate(val)
        }
    }

    // Loading and error states
    if (postingLoading) return <FormPage title="Loading..."></FormPage>
    if (postingError) return <FormPage title="Error loading posting"></FormPage>

    const posting = postingData?.postingGetSingle
    if (!posting) return <FormPage title="Posting not found"></FormPage>

    const footerLeftContent = (
        <Button clickAction={navigateToList} buttonType={ButtonType.Secondary}>
            Back to Postings
        </Button>
    )

    const footerRightContent = (
        <Button clickAction={onSubmit} buttonType={ButtonType.Success}>
            {(() => {
                // Check if there's an existing price for the selected date
                // Use UTC for both dates to avoid timezone issues
                const existingPriceForDate = priceHistoryData?.postingPriceGetHistory?.find(price =>
                    moment.utc(price.effectiveDate).isSame(moment.utc(selectedEffectiveDate), 'day')
                )

                return existingPriceForDate ? 'Update Posting' : 'Add New Posting'
            })()}
        </Button>
    )

    return (
        <FormPage
            title={`Edit Posting: ${posting.supplier.name} → ${posting.terminal.name}`}
            footerLeftContent={footerLeftContent}
            footerRightContent={footerRightContent}
        >
            {/* Current Price Form */}
            <FormTile title="Current Price">
                <div className="row">
                    <div className="col-md-4">
                        <HookedTextInput
                            label="Posting"
                            propertyKey={propName<PriceFormData>(o => o.postingPrice.cost)}
                            defaultValue={currentPrice?.cost?.toFixed(4) || "0.0000"}
                            placeholder="Enter posting price..."
                            {...commonProps}
                        />
                    </div>
                    <div className="col-md-6">
                        <HookedDateTime
                            label="Effective Date"
                            propertyKey={propName<PriceFormData>(o => o.postingPrice.effectiveDate)}
                            // defaultValue={currentPrice ? new Date(currentPrice.effectiveDate) : new Date()}
                            showTimeSelect={false}
                            defaultValue={selectedEffectiveDate}
                            // setValue={effectiveDateChanged}
                            Change={dateChange}
                            {...commonProps}
                        />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <Button
                            clickAction={onSubmit}
                            buttonType={ButtonType.Primary}
                            className={`w-100 ${styles.submitButton}`}
                        >
                            {(() => {
                                const selectedDate = selectedEffectiveDate
                                if (!selectedDate) return 'Add'

                                // Check if there's an existing price for the selected date using UTC comparison
                                const existingPriceForDate = priceHistoryData?.postingPriceGetHistory?.find(price =>
                                    moment.utc(price.effectiveDate).isSame(moment.utc(selectedDate), 'day')
                                )

                                return existingPriceForDate ? 'Update' : 'Add'
                            })()}
                        </Button>
                    </div>
                </div>

                {currentPrice && (
                    <div className="mt-3 p-3 bg-light rounded">
                        <h6 className="mb-2">Current Posting</h6>
                        <p className="mb-1">
                            <strong>${currentPrice.cost.toFixed(4)}</strong>
                            {' '}effective {moment.utc(currentPrice.effectiveDate).format('MM/DD/YYYY')}
                        </p>
                        <small className="text-muted">
                            {(() => {
                                const selectedDate = selectedEffectiveDate
                                if (!selectedDate) return 'Select a date to add a new price.'

                                // Check if there's an existing price for the selected date
                                // Use UTC for both dates to avoid timezone issues
                                const existingPriceForDate = priceHistoryData?.postingPriceGetHistory?.find(price =>
                                    moment.utc(price.effectiveDate).isSame(moment.utc(selectedDate), 'day')
                                )



                                return existingPriceForDate
                                    ? 'This will update the existing price for this date.'
                                    : 'This will create a new price for this date.'
                            })()}
                        </small>


                    </div>
                )}

                {/* Posting Archived Status */}
                <div className="row mt-3">
                    <div className="col-md-12">
                        <div className={styles.postingStatusContainer}>
                            <div>
                                <h6 className="mb-1">Posting Status</h6>
                                <small className="text-muted">
                                    {posting.isArchived
                                        ? "This posting is archived and not available for new orders."
                                        : "This posting is currently active and available for orders."
                                    }
                                </small>
                            </div>
                            <div className={styles.statusActions}>
                                <span className={`badge ${!posting.isArchived ? 'badge-success' : 'badge-secondary'} ${styles.statusBadge} ${!posting.isArchived ? styles.active : styles.inactive}`}>
                                    {!posting.isArchived ? 'ACTIVE' : 'ARCHIVED'}
                                </span>
                                <Button
                                    clickAction={handleToggleArchivedStatus}
                                    buttonType={!posting.isArchived ? ButtonType.Secondary : ButtonType.Success}
                                >
                                    {!posting.isArchived ? 'Archive' : 'Unarchive'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </FormTile>

            {/* Price History */}
            <FormTile title="Price History">
                {priceHistoryLoading ? (
                    <p>Loading price history...</p>
                ) : priceHistoryError ? (
                    <p className="text-danger">Error loading price history</p>
                ) : priceHistoryData?.postingPriceGetHistory?.length ? (
                    <div>
                        {/* Price history items */}
                        <div>
                            {(showAllPrices
                                ? priceHistoryData.postingPriceGetHistory
                                : priceHistoryData.postingPriceGetHistory.slice(0, 10)
                            ).map((price, index) => {
                                // Check if this price matches the backend-computed current price
                                const isCurrentPrice = currentPrice && price.postingPriceKey === currentPrice.postingPriceKey

                                return (
                                    <div
                                        key={price.postingPriceKey}
                                        className={`${styles.priceHistoryItem} ${isCurrentPrice ? styles.current : styles.normal}`}
                                    >
                                        <div className={styles.priceItemHeader}>
                                            <div>
                                                <strong>${price.cost.toFixed(4)}</strong> <span className="text-muted" style={{ fontSize: '0.9em' }}>Posting</span>
                                                {isCurrentPrice && (
                                                    <span className={`${styles.priceBadge} ${styles.current}`}>
                                                        CURRENT
                                                    </span>
                                                )}
                                                {moment.utc(price.effectiveDate).isAfter(moment.utc(), 'day') && (
                                                    <span className={`${styles.priceBadge} ${styles.future}`}>
                                                        FUTURE
                                                    </span>
                                                )}
                                            </div>
                                            <div className={styles.priceItemActions}>
                                                <div className={styles.priceDate}>
                                                    Effective: {moment.utc(price.effectiveDate).format('MM/DD/YYYY')}
                                                </div>
                                                <Button
                                                    clickAction={() => setCurrentlyDeletingPrice(price.postingPriceKey)}
                                                    buttonType={ButtonType.TightSecondary}
                                                >
                                                    <Trash />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Show All / Show Less button */}
                        {priceHistoryData.postingPriceGetHistory.length > 10 && (
                            <div className={styles.showAllContainer}>
                                <Button
                                    clickAction={() => setShowAllPrices(!showAllPrices)}
                                    buttonType={ButtonType.Secondary}
                                >
                                    {showAllPrices
                                        ? `Show Less (${priceHistoryData.postingPriceGetHistory.length - 10} more hidden)`
                                        : `Show All ${priceHistoryData.postingPriceGetHistory.length} Prices`
                                    }
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-muted">No price history available. Add a price above to get started.</p>
                )}
            </FormTile>

            {/* Delete Confirmation Modal */}
            {currentlyDeletingPrice && (
                <DeleteEntityModal
                    cancel={cancelDeletePrice}
                    confirm={confirmDeletePrice}
                    entityKey={currentlyDeletingPrice}
                    entityName="price"
                    orderFilter={{ orderKey: { eq: -1 } }} // Empty filter - no orders will match
                />
            )}
        </FormPage>
    )
}
